import React from 'react'
import { tableHeaderThStyle, tableBodyTdStyleRead, tableBodyTdStyleUnread, tableBodyTdStyle } from '@/constants/layout'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { dividedRoomNumber, dividedTypeName } from '@/libs/divide-list'
import { guestNameUnreadEnStyle, guestNameUnreadJaStyle } from '@/components/pages/accommodation-management/list-layout'
import { Link } from 'react-router-dom'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { useWindowSize } from 'react-use'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { SortKey } from '../../list'
import { SearchReservationList } from '@/models/reservation'

type Props = {
  reservations: SearchReservationList[]
  sortKey: SortKeyType
  onChangeSortKey: (sort: SortKeyType) => void
  listScrollRef?: React.RefObject<HTMLDivElement>
}

export const List = (props: Props) => {
  const { t, i18n } = useTranslation()
  const windows = useWindowSize()

  const goToDetailFromEntireSearch = (id: string, reservationId: string, approvedStatus: number, checkinId: string | undefined) => {
    let tabType

    if (approvedStatus === ApprovedStatusType.Reservation) {
      tabType = 'reservation'
    } else if (approvedStatus === ApprovedStatusType.Checkin && checkinId) {
      id = checkinId
      tabType = 'checkin'
    } else if (approvedStatus === ApprovedStatusType.Stay && checkinId) {
      id = checkinId
      tabType = 'stay'
    } else if (approvedStatus === ApprovedStatusType.Checkout && checkinId) {
      id = checkinId
      tabType = 'checkout'
    }

    const queryString = require('query-string')
    const queryParams = queryString.parse(window.location.search)
    delete queryParams['reservationId']

    return {
      pathname: `/${tabType}/${id}`,
      search: `${queryString.stringify(queryParams)}&reservationId=${reservationId}`,
      state: {
        fromEntireSearch: true,
      },
    }
  }

  return (
    <>
      <div ref={props.listScrollRef} css={{ overflow: 'auto', height: windows.height - 218 }}>
        <table css={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortableTableHeader
                name={SortKey.ReserveUserName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation person')}
              </SortableTableHeader>{' '}
              <SortableTableHeader
                name={SortKey.ReserveGuestName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Guest')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReservationId}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation ID')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.RoomType} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Room type')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.RoomNumber}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Room number')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ApprovedStatus}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Status')}
              </SortableTableHeader>{' '}
              <SortableTableHeader
                name={SortKey.CheckinDate}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Date of stay')}
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {props.reservations?.map((v, index) => (
              <Link
                css={[
                  v.reservationRead ? tableBodyTdStyleRead : tableBodyTdStyleUnread,
                  { display: 'table-row', ':hover': { color: '#000' } },
                ]}
                key={index}
                to={goToDetailFromEntireSearch(v.id, v.reservationId, v.approvedStatus, v.checkinId)}
              >
                <td css={[entireSearchTdStyle, { paddingLeft: 32 }]}>
                  <div css={i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle}>
                    <div css={SubscriberNameKanaStyle}>{v.nameKana ? v.nameKana : '-'}</div>
                    <div css={SubscriberNameStyle}>{v.name ? v.name : '-'}</div>
                  </div>
                </td>
                <td css={[entireSearchTdStyle, { paddingLeft: 8 }]}>
                  <div css={i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle}>
                    <div css={SubscriberNameKanaStyle}>{v.guestNameKana ? v.guestNameKana : '-'}</div>
                    <div css={SubscriberNameStyle}>{v.guestName ? v.guestName : '-'}</div>
                  </div>
                </td>
                <td css={reservationIdTdStyle}>
                  <div css={!v.reservationRead ? unreadStyle : readStyle}>
                    <div css={reservationIdStyle}>{v.reservationId ? v.reservationId : '-'}</div>
                  </div>
                </td>
                <td css={[entireSearchTdStyle, { maxWidth: 200 }]}>
                  <div css={[roomTypeStyle, !v.reservationRead ? unreadDataRoomNumberStyle : readDataRoomNumberStyle]}>
                    {dividedTypeName(v.rooms)}
                  </div>
                </td>
                <td css={[entireSearchTdStyle, { maxWidth: 150 }]}>
                  <div css={roomNumberStyle}>
                    {v.rooms.length !== 0 ? (
                      <div css={!v.reservationRead ? unreadDataRoomNumberStyle : readDataRoomNumberStyle}>{dividedRoomNumber(v.rooms)}</div>
                    ) : (
                      '-'
                    )}
                  </div>
                </td>
                <td css={[entireSearchTdStyle]}>
                  <div css={statusTagStyle}>
                    {v.approvedStatus === 0 && <div css={statusTagOrangeStyle}>{t('Reserve')}</div>}
                    {v.approvedStatus === 1 && <div css={statusTagRedStyle}>{t('Checkin')}</div>}
                    {v.approvedStatus === 2 && <div css={statusTagBlueStyle}>{t('During your stay')}</div>}
                    {v.approvedStatus === 3 && <div css={statusTagGreenStyle}>{t('Checkout')}</div>}
                  </div>
                </td>
                <td css={[entireSearchTdStyle]}>
                  <div css={!v.reservationRead ? unreadStyle : readStyle}>
                    {v.checkinDate === null ? (
                      '-'
                    ) : (
                      <>
                        {dayjs(v.checkinDate).format(t('YYYY-MM-DD'))}
                        <span style={{ paddingLeft: 5 }}>
                          {v.nights ?? 0}
                          {t('Night')}
                        </span>
                      </>
                    )}
                  </div>
                </td>
              </Link>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

const dataTextStyle = css({
  paddingRight: 16,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const SubscriberNameKanaStyle = css(dataTextStyle, {
  fontSize: 12,
  letterSpacing: 0.48,
  lineHeight: 1.5,
  color: '#676767',
})

const SubscriberNameStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 1.5,
})

const reservationIdStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
})

const roomTypeStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
})

const dataRoomNumberStyle = css(dataTextStyle, {})

const roomNumberStyle = css(dataTextStyle, {
  fontSize: 14,
  lineHeight: 2.5,
  color: '#272727',
  minWidth: 100,
})

const statusTagStyle = css({
  paddingRight: 32,
  fontSize: 12,
  width: 145,
  minWidth: 145,
})

const statusTagOrangeStyle = css({
  textAlign: 'center',
  border: '1px solid #F2A40B',
  borderRadius: 5,
  color: '#F2A40B',
  padding: '4px 10px',
})

const statusTagRedStyle = css(statusTagOrangeStyle, { color: '#f47110', borderColor: '#f47110' })
const statusTagBlueStyle = css(statusTagOrangeStyle, { color: '#3E85CC', borderColor: '#3E85CC' })
const statusTagGreenStyle = css(statusTagOrangeStyle, { color: '#7dc931', borderColor: '#7dc931' })

const unreadStyle = css({
  fontWeight: 'bold',
})

const readStyle = css({
  fontWeight: 'normal',
})

const unreadDataRoomNumberStyle = css(unreadStyle, dataRoomNumberStyle)

const readDataRoomNumberStyle = css(readStyle, dataRoomNumberStyle)

const entireSearchTdStyle = css(tableBodyTdStyle, {
  whiteSpace: 'nowrap',
})

const reservationIdTdStyle = css(entireSearchTdStyle, {
  maxWidth: 180,
  '@media(max-width: 1080px)': {
    maxWidth: 140,
  },
})
